
import { useState, useEffect } from "react";
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from "date-fns";

interface CountdownTimerProps {
  targetDate: Date;
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function CountdownTimer({ 
  targetDate, 
  className = "",
  size = "md"
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      
      if (now >= targetDate) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const days = differenceInDays(targetDate, now);
      const hours = differenceInHours(targetDate, now) % 24;
      const minutes = differenceInMinutes(targetDate, now) % 60;
      const seconds = differenceInSeconds(targetDate, now) % 60;

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000); // Update every second

    return () => clearInterval(timer);
  }, [targetDate]);

  const getDigitSize = () => {
    switch(size) {
      case "sm":
        return "text-2xl";
      case "lg":
        return "text-5xl";
      default: // md
        return "text-4xl";
    }
  };

  const getLabelSize = () => {
    switch(size) {
      case "sm":
        return "text-4xl";
      case "lg":
        return "text-6xl";
      default: // md
        return "text-5xl";
    }
  };

  const getPillPadding = () => {
    switch(size) {
      case "sm":
        return "px-4 py-2.5";
      case "lg":
        return "px-6 py-4";
      default: // md
        return "px-5 py-3.5";
    }
  };

  const getContainerPadding = () => {
    switch(size) {
      case "sm":
        return "px-10 py-8";
      case "lg":
        return "px-16 py-12";
      default: // md
        return "px-14 py-10";
    }
  };

  return (
    <div className={`inline-block rounded-[32px] ${getContainerPadding()} shadow-[0_10px_30px_rgba(109,40,217,0.2)] border border-purple-900/30 ${className}`} style={{ backgroundColor: '#0b111d' }}>
      <div className="flex flex-col items-center justify-center relative">
        <div className={`${getLabelSize()} font-apple font-bold tracking-tight bg-gradient-to-r from-purple-400/90 to-purple-500/90 bg-clip-text text-transparent mb-1 relative z-0`}>
          New Books
        </div>
        
        <div className="relative rounded-xl p-[3px] bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.25)] z-10 -my-2">
          <div className={`bg-[#0a0612] rounded-xl ${getPillPadding()} shadow-[0_6px_20px_rgba(0,0,0,0.85)]`}>
            <div className={`flex items-center gap-3 ${getDigitSize()} font-bold text-white font-apple-numbers tabular-nums`}>
              {timeLeft.days > 0 && (
                <>
                  <span>{timeLeft.days.toString().padStart(2, '0')}</span>
                  <span className="text-purple-400">:</span>
                </>
              )}
              <span>{timeLeft.hours.toString().padStart(2, '0')}</span>
              <span className="text-purple-400">:</span>
              <span>{timeLeft.minutes.toString().padStart(2, '0')}</span>
              <span className="text-purple-400">:</span>
              <span>{timeLeft.seconds.toString().padStart(2, '0')}</span>
            </div>
          </div>
        </div>
        
        <div className={`${getLabelSize()} font-apple font-bold tracking-tight text-purple-600/70 -mt-1 mb-0 relative z-0`}>
          Premiere in
        </div>
      </div>
    </div>
  );
}
